import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, ArrowLeft, KeyRound, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '@/components/SEO';

// Use window.location.origin to ensure we always call the correct backend
const API = `${window.location.origin}/api`;
const LOGO_URL = "/images/dea-wordmark.png"; // Full "Dea" logo for auth pages

export default function Login() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  
  // Get redirect parameter
  const redirectTo = searchParams.get('redirect') || '/dashboard';
  
  // Password Reset State
  const [showResetFlow, setShowResetFlow] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1 = enter email, 2 = enter code + new password
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back, sis!');
      navigate(redirectTo);
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      toast.error('Please enter your email');
      return;
    }
    
    setResetLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email: resetEmail });
      toast.success('Reset code sent! Check the response below.');
      // For demo purposes, show the code (in production, it would be emailed)
      if (response.data.reset_code) {
        setResetCode(response.data.reset_code);
        toast.info(`Your reset code: ${response.data.reset_code}`, { duration: 10000 });
      }
      setResetStep(2);
    } catch (error) {
      toast.error('Failed to send reset code. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetCode || !newPassword) {
      toast.error('Please enter the reset code and new password');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResetLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: resetEmail,
        reset_code: resetCode,
        new_password: newPassword
      });
      toast.success('Password reset successfully! You can now log in.');
      setShowResetFlow(false);
      setResetStep(1);
      setEmail(resetEmail);
      setResetEmail('');
      setResetCode('');
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title="Sign In"
        description="Sign in to your Dea account and continue your transformation journey with Dea."
      />
      <div className="min-h-screen bg-[#FFFCF7] flex">
      {/* Left - Portrait Video */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#EAD8C2]/30 overflow-hidden">
        <video
          id="login-video"
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          playsInline
          onEnded={() => {
            const btn = document.getElementById('login-play-btn');
            const video = document.getElementById('login-video');
            if (btn) btn.style.display = 'flex';
            if (video) video.muted = true;
          }}
        >
          <source src="https://storage.googleapis.com/msgsndr/RVXeSVbF3U7E56SwQumk/media/698e4aeaa44964c4d7bbbfe9.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-[#726158]/60 to-transparent" />
        
        {/* Play button overlay */}
        <button
          id="login-play-btn"
          onClick={() => {
            const video = document.getElementById('login-video');
            const btn = document.getElementById('login-play-btn');
            if (video) {
              video.muted = false;
              video.currentTime = 0;
              video.play();
              if (btn) btn.style.display = 'none';
            }
          }}
          className="absolute inset-0 flex items-center justify-center z-10 group"
        >
          <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/30 transition-all group-hover:scale-110">
            <Play className="h-10 w-10 text-white ml-1" />
          </div>
        </button>
        
        <div className="absolute bottom-12 left-12 right-12 z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <p className="font-serif font-brand-accent text-3xl text-[#FFFCF7] mb-4">Dear Dream Me...</p>
            <p className="text-[#FFFCF7]/90 text-lg max-w-md">
              "You wake up every morning knowing exactly what to do that day to move closer to your 90-day goal."
            </p>
          </motion.div>
        </div>
      </div>

      {/* Right - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#FFFCF7]">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-md space-y-8"
        >
          <div className="text-center lg:text-left">
            <Link to="/" className="inline-block mb-8">
              <img src={LOGO_URL} alt="Dea" className="h-16 w-auto" />
            </Link>
            <h1 className="font-serif text-3xl text-[#726158] mb-2">Welcome Back, Sis</h1>
            <p className="text-[#AA9283]">Ready to continue crushing those goals?</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#AA9283]">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#B1897C]" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-light pl-10"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#AA9283]">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#B1897C]" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-light pl-10 pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B1897C] hover:text-[#AA9283] transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="text-right">
              <button
                type="button"
                onClick={() => setShowResetFlow(true)}
                className="text-sm text-[#87A6A7] hover:text-[#CAAB84] transition-colors"
              >
                Forgot Password?
              </button>
            </div>

            <Button
              type="submit"
              className="w-full btn-gold py-6"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? <span className="spinner" /> : 'Sign In'}
            </Button>
          </form>

          <p className="text-center text-[#AA9283]">
            Don't have an account?{' '}
            <Link to="/signup" className="text-[#87A6A7] hover:text-[#CAAB84] font-medium">
              Join Us
            </Link>
          </p>
        </motion.div>

        {/* Password Reset Modal */}
        {showResetFlow && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => { setShowResetFlow(false); setResetStep(1); }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-[#FFFCF7] rounded-2xl p-6 max-w-md w-full"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#87A6A7]/20 flex items-center justify-center">
                  <KeyRound className="h-6 w-6 text-[#87A6A7]" />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-[#726158]">Reset Password</h3>
                  <p className="text-sm text-[#AA9283]">
                    {resetStep === 1 ? 'Enter your email to get a reset code' : 'Enter the code and your new password'}
                  </p>
                </div>
              </div>

              {resetStep === 1 ? (
                <form onSubmit={handleRequestReset} className="space-y-4">
                  <div>
                    <Label className="text-[#AA9283]">Email Address</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#B1897C]" />
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        className="input-light pl-10"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowResetFlow(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 btn-gold"
                      disabled={resetLoading}
                    >
                      {resetLoading ? <span className="spinner" /> : 'Send Reset Code'}
                    </Button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div>
                    <Label className="text-[#AA9283]">Reset Code</Label>
                    <Input
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value)}
                      className="input-light mt-1"
                      maxLength={6}
                    />
                  </div>
                  <div>
                    <Label className="text-[#AA9283]">New Password</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#B1897C]" />
                      <Input
                        type="password"
                        placeholder="At least 6 characters"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="input-light pl-10"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setResetStep(1)}
                      className="flex-1"
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" /> Back
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 btn-gold"
                      disabled={resetLoading}
                    >
                      {resetLoading ? <span className="spinner" /> : 'Reset Password'}
                    </Button>
                  </div>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </div>
    </div>
    </>
  );
}
